import { PlayerFiltersPanel } from "@/pages/Players/FiltersPanel/PlayerFiltersPanel";
import {
  RangeFilterType,
  SelectorFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import {
  applyEvolutionBuilderFilters,
  evolutionBuilderPlayStyleFiltersSelector,
  evolutionBuilderPlayerFiltersSelector,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import {
  activeFiltersOf,
  applyFilterUpdate,
  clearAllFiltersIn,
  clearFilterIn,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

interface Props {
  onFiltersUpdated: () => void;
}

const cloneFilters = (
  filters: (RangeFilterType | SelectorFilterType)[],
): (RangeFilterType | SelectorFilterType)[] => {
  return filters.map((filter) => {
    if (filter.type === "RANGE_FILTER") {
      return { ...filter };
    } else {
      return {
        ...filter,
        options: [...filter.options],
        selectedOptions: [...filter.selectedOptions],
      };
    }
  });
};

const arraysEqual = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const hasPlayerFilterChanged = (
  local: (RangeFilterType | SelectorFilterType)[],
  redux: (RangeFilterType | SelectorFilterType)[],
) =>
  local.some((localFilter) => {
    const reduxFilter = redux.find((f) => f.key === localFilter.key);
    if (!reduxFilter) return true;

    if (
      localFilter.type === "RANGE_FILTER" &&
      reduxFilter.type === "RANGE_FILTER"
    ) {
      return (
        localFilter.minValue !== reduxFilter.minValue ||
        localFilter.maxValue !== reduxFilter.maxValue
      );
    }

    if (
      localFilter.type === "SELECTOR_FILTER" &&
      reduxFilter.type === "SELECTOR_FILTER"
    ) {
      if (
        localFilter.selectedOptions.length !==
        reduxFilter.selectedOptions.length
      ) {
        return true;
      }
      const localSet = new Set(localFilter.selectedOptions);
      return reduxFilter.selectedOptions.some((opt) => !localSet.has(opt));
    }

    return false;
  });

const hasPlayStyleFilterChanged = (
  local: {
    excludedPlayStyles: number[];
    excludedPlayStylesPlus: number[];
  },
  redux: {
    excludedPlayStyles: number[];
    excludedPlayStylesPlus: number[];
  },
) =>
  !arraysEqual(local.excludedPlayStyles, redux.excludedPlayStyles) ||
  !arraysEqual(local.excludedPlayStylesPlus, redux.excludedPlayStylesPlus);

export const EvolutionBuilderFilters = forwardRef<HTMLDivElement, Props>(
  ({ onFiltersUpdated }, ref) => {
    const { t } = useTranslation(["players", "common"]);
    const dispatch = useDispatch<AppDispatch>();
    const reduxFilters = useSelector(evolutionBuilderPlayerFiltersSelector);
    const reduxPlayStyleFilters = useSelector(
      evolutionBuilderPlayStyleFiltersSelector,
    );

    // Local state initialized from Redux (deep cloned to avoid mutations)
    const [localFilters, setLocalFilters] = useState<
      (RangeFilterType | SelectorFilterType)[]
    >(() => cloneFilters(reduxFilters));

    const [localExcludedPlayStyles, setLocalExcludedPlayStyles] = useState(
      () => [...reduxPlayStyleFilters.excludedPlayStyles],
    );
    const [localExcludedPlayStylesPlus, setLocalExcludedPlayStylesPlus] =
      useState(() => [...reduxPlayStyleFilters.excludedPlayStylesPlus]);

    // Sync local state when Redux changes externally (e.g. FiltersRow chip removal)
    useEffect(() => {
      setLocalFilters(cloneFilters(reduxFilters));
    }, [reduxFilters]);

    useEffect(() => {
      setLocalExcludedPlayStyles([...reduxPlayStyleFilters.excludedPlayStyles]);
      setLocalExcludedPlayStylesPlus([
        ...reduxPlayStyleFilters.excludedPlayStylesPlus,
      ]);
    }, [reduxPlayStyleFilters]);

    const localFiltersRef = useRef(localFilters);
    localFiltersRef.current = localFilters;
    const localExcludedPlayStylesRef = useRef(localExcludedPlayStyles);
    localExcludedPlayStylesRef.current = localExcludedPlayStyles;
    const localExcludedPlayStylesPlusRef = useRef(localExcludedPlayStylesPlus);
    localExcludedPlayStylesPlusRef.current = localExcludedPlayStylesPlus;

    const localPlayStyleFilters = {
      excludedPlayStyles: localExcludedPlayStyles,
      excludedPlayStylesPlus: localExcludedPlayStylesPlus,
    };

    const hasChanges = useMemo(
      () =>
        hasPlayerFilterChanged(localFilters, reduxFilters) ||
        hasPlayStyleFilterChanged(localPlayStyleFilters, reduxPlayStyleFilters),
      [
        localFilters,
        reduxFilters,
        localPlayStyleFilters,
        reduxPlayStyleFilters,
      ],
    );

    // Keep the latest `hasChanges` readable inside the unmount-only cleanup.
    const hasChangesRef = useRef(hasChanges);
    hasChangesRef.current = hasChanges;

    // The panel has no Apply of its own: closing it applies the draft.
    useEffect(() => {
      return () => {
        if (!hasChangesRef.current) return;
        dispatch(
          applyEvolutionBuilderFilters({
            playerFilters: localFiltersRef.current,
            excludedPlayStyles: localExcludedPlayStylesRef.current,
            excludedPlayStylesPlus: localExcludedPlayStylesPlusRef.current,
          }),
        );
        onFiltersUpdated();
      };
    }, []);

    // Edits land on a copy of the draft, through the same helpers as the store.
    const editDraft = (edit: (filters: typeof localFilters) => void) =>
      setLocalFilters((prev) => {
        const next = cloneFilters(prev);
        edit(next);
        return next;
      });

    // The builder has one player picked: filters that name a player's club,
    // nation or card don't apply to the evolutions it can take.
    const hidden = [
      "price",
      "metaRating",
      "pastAndPresentClubs",
      "preferredFoot",
      "country",
      "league",
      "club",
      "rarity",
      "body-types",
      "gender",
    ];
    const activeCount = activeFiltersOf(localFilters).length;

    return (
      <div ref={ref}>
        <PlayerFiltersPanel
          filters={localFilters}
          activeCount={activeCount}
          summary={
            activeCount > 0
              ? t("filters.panel.activeCount", { count: activeCount })
              : t("common:noFiltersApplied")
          }
          hidden={hidden}
          onUpdateFilter={(update) =>
            editDraft((f) => applyFilterUpdate(f, update))
          }
          onClearFilter={(key) => editDraft((f) => clearFilterIn(f, key))}
          onClearAll={() => editDraft(clearAllFiltersIn)}
          excluded={{
            playStyles: localExcludedPlayStyles,
            playStylesPlus: localExcludedPlayStylesPlus,
            onChange: (key, ids) => {
              if (key === "playStyles") setLocalExcludedPlayStyles(ids);
              else setLocalExcludedPlayStylesPlus(ids);
            },
          }}
        />
      </div>
    );
  },
);
